import { PackageCheck, Truck, UserRoundCheck } from "lucide-react";
import type { Task } from "./warehouse-types";
import type { useWarehouseTaskWorkspace } from "./useWarehouseTaskWorkspace";
import { fmt, routeLabel, taskInstruction, labels } from "./warehouse-presentation";
export function WarehouseTaskView({task, workspace}: {
 task: Task; workspace: ReturnType<typeof useWarehouseTaskWorkspace>;
}) {
 const { quantity, setQuantity, note, setNote, saving, assignmentOpen, assignees,
 assigneeId, setAssigneeId, assigneesLoading, availableActions, primaryActions,
 exceptionActions, quantityNeeded, physicalConfirmationAvailable, stagingConfirmation,
 canManageAssignment, toggleAssignment, assign, save } = workspace;
  return (
    <article
      id={`warehouse-task-${task.id}`}
      tabIndex={-1}
      className={`warehouse-task-card is-${task.priority.toLowerCase()}`}
    >
      <div className="warehouse-task-icon">
        {task.routeLeg === "VERIFY_STAGING" ? (
          <PackageCheck size={20} />
        ) : (
          <Truck size={20} />
        )}
      </div>
      <div className="warehouse-task-main">
        <span>
          {task.workOrderNo} · {task.productCode} ·{" "}
          {task.lineName || task.lineCode}
        </span>
        <strong>
          {task.materialCode} · {task.materialName}
        </strong>
        <small>
          {routeLabel(task)}
          {task.locationCode ? ` · Lokasyon ${task.locationCode}` : ""} · Üretim{" "}
          {task.plannedStartAt
            ? new Intl.DateTimeFormat("tr-TR", {
                dateStyle: "short",
                timeStyle: "short",
              }).format(new Date(task.plannedStartAt))
            : "tarihi yok"}
        </small>
        <p className="warehouse-task-instruction">{taskInstruction(task)}</p>
      </div>
      <div className="warehouse-task-quantities">
        <span className="is-action-quantity">
          {stagingConfirmation ? "Bu sevkten teslim alınacak" : "Hazırlanacak"}{" "}
          <strong>{fmt(task.requestedQuantity, task.unit)}</strong>
        </span>
        <details>
          <summary>Hesap ayrıntısı</summary>
          <div>
            <span>
              Üretim ihtiyacı{" "}
              <strong>{fmt(task.requiredQuantity, task.unit)}</strong>
            </span>
            <span>
              Mevcut cep stok{" "}
              <strong>{fmt(task.stagingSystemQuantity, task.unit)}</strong>
            </span>
            {task.targetOpenInboundQuantity > 0 ? (
              <span>
                Açık giriş{" "}
                <strong>{fmt(task.targetOpenInboundQuantity, task.unit)}</strong>
              </span>
            ) : null}
          </div>
        </details>
      </div>
      <div className="warehouse-task-status">
        <strong>{labels[task.status] ?? task.status}</strong>
        <span>
          {task.claimedAt
            ? task.assignedToName
            : task.assignedToName
              ? `${task.assignedToName} · kabul bekliyor`
              : "Sahiplenilmedi"}
        </span>
        <span>
          Son işlem{" "}
          {new Intl.DateTimeFormat("tr-TR", {
            dateStyle: "short",
            timeStyle: "short",
          }).format(new Date(task.updatedAt))}
        </span>
        {task.completedAt ? (
          <span>
            Teyit{" "}
            {new Intl.DateTimeFormat("tr-TR", {
              dateStyle: "short",
              timeStyle: "short",
            }).format(new Date(task.completedAt))}
          </span>
        ) : null}
        {task.note ? <p className="warehouse-task-note">{task.note}</p> : null}
      </div>
      {availableActions.length ? (
        <div className="warehouse-task-actions">
          <div className="warehouse-task-fields">
            {quantityNeeded ? (
              <label>
                <span>
                  {physicalConfirmationAvailable
                    ? "Teslim sonrası cep depodaki toplam"
                    : "Hazırlanan miktar"}
                </span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  placeholder={`Toplam miktar (${task.unit})`}
                  aria-label={`${task.materialCode} işlem miktarı`}
                />
              </label>
            ) : null}
            <label>
              <span>
                {physicalConfirmationAvailable
                  ? "Not (teyitte isteğe bağlı)"
                  : "Kısa işlem notu"}
              </span>
              <input
                value={note}
                maxLength={1000}
                onChange={(event) => setNote(event.target.value)}
                placeholder={physicalConfirmationAvailable ? "Varsa fark veya açıklama" : "Ne yaptığınızı kısaca yazın"}
                aria-label={`${task.materialCode} işlem açıklaması`}
              />
            </label>
          </div>
          <div className="warehouse-task-primary-actions">
            {canManageAssignment ? (
              <button
                type="button"
                disabled={saving || assigneesLoading}
                aria-expanded={assignmentOpen}
                onClick={() => void toggleAssignment()}
              >
                <UserRoundCheck size={15} />
                {assigneesLoading
                  ? "Sorumlular yükleniyor"
                  : task.assignedToName
                    ? "Sorumluyu değiştir"
                    : "Sorumlu ata"}
              </button>
            ) : null}
            {primaryActions.map((action) => (
              <button
                type="button"
                className="is-primary"
                key={action.status}
                disabled={saving}
                onClick={() => void save(action.status)}
              >
                {action.label}
              </button>
            ))}
            {exceptionActions.length ? (
              <details className="warehouse-task-exceptions">
                <summary>Sorun bildir</summary>
                <div>
                  <span>Önce sorunu açıklayan kısa bir not yazın.</span>
                  {exceptionActions.map((action) => (
                    <button
                      type="button"
                      key={action.status}
                      disabled={saving}
                      onClick={() => void save(action.status)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </details>
            ) : null}
          </div>
          {assignmentOpen ? (
            <div className="warehouse-task-assignment">
              {assignees.length ? (
                <>
                  <label htmlFor={`task-assignee-${task.id}`}>Sorumlu</label>
                  <select
                    id={`task-assignee-${task.id}`}
                    value={assigneeId}
                    disabled={saving}
                    onChange={(event) => setAssigneeId(event.target.value)}
                  >
                    {assignees.map((assignee) => (
                      <option key={assignee.id} value={assignee.id}>
                        {assignee.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={saving || !assigneeId}
                    onClick={() => void assign()}
                  >
                    {saving ? "Atanıyor…" : "Atamayı kaydet"}
                  </button>
                </>
              ) : (
                <span role="status">
                  Bu depo ve görev rolü için aktif sorumlu bulunamadı.
                </span>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
